import { Typography } from '@euler/app/components/typography';
import {
  CardLoading,
  CardNotice,
} from '@euler/app/flows/ai-suite/components/ChatCard';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  BiSheetUploadView,
  BiUploadSheetView,
  kBiCsvSheetName,
} from '@euler/model/bi/upload';
import { memo } from 'react';
import { View } from 'react-native';
import { BiProposalTiles } from '../../bi-insight-proposals/BiProposalTiles';
import { BiProposalSelection } from '../../bi-insight-proposals/functions/useBiProposalSelection';
import { ColumnConfirm } from '../column-confirm/ColumnConfirm';
import { biRowsColumns } from '../functions/biUploadLabels';
import { biSheetPhase } from '../functions/biUploadPhase';
import { biSheetStepText } from '../functions/biUploadSubtitle';

/** One selected sheet on its way to a dataset, by its own step. */
export const SheetSection = memo(
  ({
    fileName,
    sheet,
    upload,
    selection,
  }: {
    fileName: string;
    sheet: BiUploadSheetView;
    upload: BiSheetUploadView;
    /** The proposals ticked across every sheet of the file. */
    selection: BiProposalSelection;
  }) => {
    const { rpx } = useResponsiveContext();
    const phase = biSheetPhase(upload);
    const named = upload.sheet && upload.sheet !== kBiCsvSheetName;
    return (
      <View style={{ gap: rpx(10) }}>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'baseline',
            gap: rpx(8),
            paddingTop: rpx(10),
            borderTopWidth: 1,
            borderTopColor: aiColors.border,
          }}
        >
          <Typography.Text
            medium
            size={rpx(14)}
            color={aiColors.foreground}
            numberOfLines={1}
            style={{ flexShrink: 1 }}
          >
            {named ? upload.sheet : fileName}
          </Typography.Text>
          <Typography.Text size={rpx(11)} color={aiColors.mutedForeground}>
            {biRowsColumns(sheet.rowCount, sheet.columnCount)}
          </Typography.Text>
          <View style={{ flex: 1 }} />
          <Typography.Text size={rpx(11)} color={aiColors.mutedForeground}>
            {biSheetStepText(upload)}
          </Typography.Text>
        </View>
        {phase.kind === 'working' ? (
          <CardLoading label={phase.label} />
        ) : phase.kind === 'confirm' && upload.schema ? (
          <ColumnConfirm
            key={`${upload.id}:${upload.generation}`}
            upload={upload}
            schema={upload.schema}
          />
        ) : phase.kind === 'proposals' ? (
          phase.draftIds.length ? (
            <BiProposalTiles draftIds={phase.draftIds} selection={selection} />
          ) : (
            <CardNotice text="数据已导入，暂时没有挑出合适的图表。可以直接提问来分析它。" />
          )
        ) : phase.kind === 'failed' ? (
          <CardNotice tone="danger" text={phase.message} />
        ) : (
          <CardNotice text="这份数据已删除。" />
        )}
      </View>
    );
  },
);
